import {Component, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { User } from 'src/app/identity/user.model';
import { Identity } from '../identity.model';
import { IdentityService } from '../identity.service';
import { AuthService } from 'src/app/auth/auth.service';
import {Esito, GenericResponse} from 'src/app/api-interfaces';
import { combineLatest, Observable, Subject } from 'rxjs';

import {FuncsService, emailsDontMatchValidator, phonesDontMatchValidator} from 'src/app/shared/funcs.service';
import {LogService} from 'src/app/shared/log.service';
import {ADDRESS_TYPE_CHOICES} from 'src/app/shared/address-type';
import {environment} from 'src/environments/environment';
import {EditIdentityService} from './edit-identity.service';
import {map, startWith, tap} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TimerService} from '../../shared/timer/timer.service';

@UntilDestroy()
@Component({
  selector: 'app-edit-identity',
  templateUrl: './edit-identity.component.html',
  styleUrls: ['./edit-identity.component.sass'],
  providers: [EditIdentityService]
})
export class EditIdentityComponent implements OnInit, OnDestroy {

  constructor(
    private funcs: FuncsService,
    private fb: UntypedFormBuilder,
    private identityService: IdentityService,
    private authService: AuthService,
    private modalService: NzModalService,
    private logger: LogService,
    public timerService: TimerService,
    private editIdentityService: EditIdentityService
  ) {
  }

  readonly issuers$ = this.editIdentityService.issuers$;
  readonly areLoadingIssuers$ = this.editIdentityService.areLoadingIssuers$;
  readonly canLoadMoreIssuer$ = this.editIdentityService.canLoadMoreIssuer$;
  readonly showIssuerSpinner$ = combineLatest([this.areLoadingIssuers$, this.canLoadMoreIssuer$]).pipe(map(([areLoadingIssuers, canLoadMoreIssuer]) => areLoadingIssuers || canLoadMoreIssuer));

  readonly countries$ = this.editIdentityService.countries$;
  readonly areLoadingCountries$ = this.editIdentityService.areLoadingCountries$;
  readonly canLoadMoreCountry$ = this.editIdentityService.canLoadMoreCountry$;
  readonly showCountrySpinner$ = combineLatest([this.areLoadingCountries$, this.canLoadMoreCountry$]).pipe(map(([areLoadingCountries, canLoadMoreCountry]) => areLoadingCountries || canLoadMoreCountry));
  readonly provincies$ = this.editIdentityService.provincies$;
  readonly areLoadingProvincies$ = this.editIdentityService.areLoadingProvincies$;
  readonly canLoadMoreProvince$ = this.editIdentityService.canLoadMoreProvince$;
  readonly showProvinceSpinner$ = combineLatest([this.areLoadingProvincies$, this.canLoadMoreProvince$]).pipe(map(([areLoadingProvincies, canLoadMoreProvince]) => areLoadingProvincies || canLoadMoreProvince));
  readonly cities$ = this.editIdentityService.cities$;
  readonly areLoadingCities$ = this.editIdentityService.areLoadingCities$;
  readonly canLoadMoreCity$ = this.editIdentityService.canLoadMoreCity$;
  readonly showCitySpinner$ = combineLatest([this.areLoadingCities$, this.canLoadMoreCity$]).pipe(map(([areLoadingCities, canLoadMoreCity]) => areLoadingCities || canLoadMoreCity));

  isSpinning = false;
  user: User;
  identity: Identity;
  modifyEmailForm!: UntypedFormGroup;
  modifyMobileForm!: UntypedFormGroup;
  modifyAddressForm!: UntypedFormGroup;
  modifyCompanyForm!: UntypedFormGroup;
  modifyDigitalAddressForm!: UntypedFormGroup;
  modifyDocumentForm!: UntypedFormGroup;
  public emailChange = '';
  public emailChangeConfirm = '';
  public mobileChange = '';
  public mobileChangeConfirm = '';
  public username = '';
  public otp = '';
  public otpCode = '';
  private tplModal?: NzModalRef;
  addressTypeChoices: { key: string, label: string }[];
  objTelChangeInput: any;
  objTelConfirmInput: any;

  onErcLostEventSubject: Subject<void> = new Subject<void>();
  nzFilterOption = () => true;

  ngOnInit(): void {
    this.user = this.authService.getCurrentUser();
    this.authService.currentUserChanged.subscribe((data: User) => {
      this.user = data;
    });
    this.identity = this.identityService.getCurrentIdentity();
    this.identityService.currentIdentityChanged.subscribe((data: Identity) => {
      this.identity = data;
    });

    this.modifyEmailForm = this.fb.group({
      emailChange: [null,
        [Validators.required,
          Validators.pattern(RegExp(this.funcs.getValidationPattern('email')))]
      ],
      emailChangeConfirm: [null,
        [Validators.required,
          Validators.pattern(RegExp(this.funcs.getValidationPattern('email')))]
      ],
      otpCode: [null, [Validators.required]],
    }, {validators: emailsDontMatchValidator});

    this.modifyMobileForm = this.fb.group({
      mobileChange: [null,
        [Validators.required,
          Validators.pattern(RegExp(this.funcs.getValidationPattern('phone')))]],
      mobileChangeConfirm: [null,
        [Validators.required,
          Validators.pattern(RegExp(this.funcs.getValidationPattern('phone')))]],
      otpCode: [null, [Validators.required]],
    }, {validators: phonesDontMatchValidator});

    this.modifyAddressForm = this.fb.group({
      addressTypeChange: [null, [Validators.required, ]],
      addressNameChange: [null, [Validators.required, ]],
      addressNumChange: [null, [Validators.required, ]],
      capChange: [null,
        [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(5), Validators.maxLength(5)]],
      cityChange: [null, [Validators.required, ]],
      provinceChange: [null, [Validators.required, ]],
      countryChange: [null, [Validators.required, ]],
      otpCode: [null, [Validators.required]],
    });

    this.modifyCompanyForm = this.fb.group({
      postal_code: [null, [Validators.required, ]],
      address_type: [null, [Validators.required, ]],
      address_name: [null, [Validators.required, ]],
      address_num: [null, [Validators.required, ]],
      city: [null, [Validators.required, ]],
      province: [null, [Validators.required, ]],
      country: [null, [Validators.required]],
      otpCode: [null, [Validators.required]],
    });

    this.modifyDigitalAddressForm = this.fb.group({
      addressChange: [null, [Validators.required, ]],
      otpCode: [null, [Validators.required]],
    });

    this.modifyDocumentForm = this.fb.group({
      document_typeChange: [null,
        [Validators.required]],
      document_numberChange: [null,
        [Validators.required, ]],
      issueDateChange: [null,
        [Validators.required, ]],
      issuerNameChange: [null,
        [Validators.required, ]],
      issuerTypeChange: [null,
        [Validators.required, ]],
      document_expiration_dateChange: [null,
        [Validators.required, ]],
      issueCountryChange: [null,
        [Validators.required, ]],
      otpCode: [null, [Validators.required]],
    });
    this.addressTypeChoices = ADDRESS_TYPE_CHOICES;

    this.onIssuerValueChange$().subscribe();
    this.registerOnIssuerTypeChangeStatus$().subscribe();
    this.registerOnIssuerTypeChangeValue$().subscribe();

    this.onCountryValueChange$().subscribe();
    this.registerOnCountryStatusChange$().subscribe();
    this.registerOnCountryValueChange$().subscribe();

    this.onProvinceValueChange$().subscribe();
    this.registerOnProvinceStatusChange$().subscribe();
    this.registerOnProvinceValueChange$().subscribe();

    this.onCityValueChange$().subscribe();
  }

  onEditEmail(emailModal: TemplateRef<{}>, emailModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyEmailForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: emailModal,
      nzFooter: emailModalFooter,
    });
  }

  onEditMobile(mobileModal: TemplateRef<{}>, mobileModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyMobileForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: mobileModal,
      nzFooter: mobileModalFooter,
    });

  }

  onEditDocument(documentModal: TemplateRef<{}>, documentModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyDocumentForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: documentModal,
      nzFooter: documentModalFooter,
    });
  }

  onEditAddress(addressModal: TemplateRef<{}>, addressModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyAddressForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: addressModal,
      nzFooter: addressModalFooter,
      nzOnCancel: () => {
        this.modifyAddressForm.reset();
      }
    });
  }

  onEditDigitalAddress(digitalAddressModal: TemplateRef<{}>, digitalAddressModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyDigitalAddressForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: digitalAddressModal,
      nzFooter: digitalAddressModalFooter
    });
  }

  onEditCompany(companyModal: TemplateRef<{}>, companyModalFooter: TemplateRef<{}>): void {
    if (!environment.enableSharedTimer) {
      this.timerService.resetTimerSendOTP();
    }
    this.modifyCompanyForm.reset();
    this.tplModal = this.modalService.create({
      nzTitle: $localize`:Identity.EditIdentity.TitleModalEditData|title for edit data modal@@Identity.EditIdentity.TitleModalEditData:Modifica dati di contatto`,
      nzContent: companyModal,
      nzFooter: companyModalFooter,
    });
  }

  onRecoveryEmergencyCode(): void {
    this.onErcLostEventSubject.next();
  }

  onSendEmailChange(): void {
    for (const i in this.modifyEmailForm.controls) {
      this.modifyEmailForm.controls[i].markAsDirty();
      this.modifyEmailForm.controls[i].updateValueAndValidity();
    }

    const emailChange = this.modifyEmailForm.value.emailChange;
    const emailChangeConfirm = this.modifyEmailForm.value.emailChangeConfirm;
    const otpCode = this.modifyEmailForm.value.otpCode;

    if (!emailChange && !emailChangeConfirm && !otpCode) {
      this.logger.log('manca un campo obbligatorio');
      return;
    }

    if (emailChange !== emailChangeConfirm) {
      this.logger.log(`le email non coincidono ${emailChange} ${emailChangeConfirm}`);
      return;
    }

    this.isSpinning = true;

    this.identityService.changeEmail(
      this.identityService.getCurrentIdentity().level,
      emailChange,
      otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.tplModal.close();
            this.modalService.create({
              nzTitle: 'Conferma',
              nzContent: 'Il cambio e-mail è andato a buon fine. Per attivare il nuovo indirizzo cliccare sul link che le abbiamo inviato via mail.',
              nzCancelText: null
            });
            this.user.email = emailChange;
            if (environment.resetTimerWhenSubmit) {
              this.timerService.resetTimerSendOTP();
            }
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  onSendMobileChange(): void {
    for (const i in this.modifyMobileForm.controls) {
      this.modifyMobileForm.controls[i].markAsDirty();
      this.modifyMobileForm.controls[i].updateValueAndValidity();
    }

    const mobileChange = this.modifyMobileForm.value.mobileChange;
    const mobileChangeConfirm = this.modifyMobileForm.value.mobileChangeConfirm;
    const otpCode = this.modifyMobileForm.value.otpCode;

    if (!mobileChange && !mobileChangeConfirm && !otpCode) {
      this.logger.log(`manca un campo obbligatorio ${mobileChange} ${mobileChangeConfirm} ${otpCode}`);
      return;
    }

    if (mobileChange !== mobileChangeConfirm) {
      this.logger.log(`i numeri non coincidono ${mobileChange} ${mobileChangeConfirm}`);
      return;
    }

    this.isSpinning = true;

    this.identityService.changePhoneNumber(
      this.identityService.getCurrentIdentity().level,
      mobileChange,
      otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.user.phone_number = mobileChange;
            this.authService.setCurrentUser(this.user);
            this.modifyMobileForm.reset();
            this.tplModal.close();
          }
          if (environment.resetTimerWhenSubmit) {
            this.timerService.resetTimerSendOTP();
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  onValidatePhone(isMobileChange: boolean = true): void {
    if (isMobileChange) {
      this.mobileChange = this.objTelChangeInput.a.value;
    } else {
      this.mobileChangeConfirm = this.objTelConfirmInput.a.value;
    }
  }

  telInputObject(isMobileChange: boolean = true, obj: any): void {
    if (isMobileChange) {
      this.objTelChangeInput = obj;
    } else {
      this.objTelConfirmInput = obj;
    }
  }

  onCountryChange(isMobileChange: boolean = true, data: any): void {
    this.onValidatePhone(isMobileChange);
    this.modifyMobileForm.patchValue({
      mobileChange: this.mobileChange,
      mobileChangeConfirm: this.mobileChangeConfirm
    });
  }

  getValidationErrorMessage(errors: any): string {
    return this.funcs.getValidationErrorMessage(errors);
  }


  onSendAddressChange(): void {
    for (const i in this.modifyAddressForm.controls) {
      this.modifyAddressForm.controls[i].markAsDirty();
      this.modifyAddressForm.controls[i].updateValueAndValidity({emitEvent: false});
    }

    this.isSpinning = true;

    this.identityService.changeAddress(
      this.modifyAddressForm.value.addressTypeChange,
      this.modifyAddressForm.value.addressNameChange,
      this.modifyAddressForm.value.addressNumChange,
      this.modifyAddressForm.value.capChange,
      this.modifyAddressForm.value.cityChange,
      this.modifyAddressForm.value.provinceChange,
      this.modifyAddressForm.value.countryChange,
      this.modifyAddressForm.value.otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.modifyAddressForm.reset();
            this.tplModal.close();
          }
          if (environment.resetTimerWhenSubmit) {
            this.timerService.resetTimerSendOTP();
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  onSendCompanyChange(): void {
    for (const i in this.modifyCompanyForm.controls) {
      this.modifyCompanyForm.controls[i].markAsDirty();
      this.modifyCompanyForm.controls[i].updateValueAndValidity();
    }

    this.isSpinning = true;

    this.identityService.changeCompany(
      this.modifyCompanyForm.value.address_type,
      this.modifyCompanyForm.value.address_name,
      this.modifyCompanyForm.value.address_num,
      this.modifyCompanyForm.value.postal_code,
      this.modifyAddressForm.value.city,
      this.modifyCompanyForm.value.province,
      this.modifyCompanyForm.value.country,
      this.modifyCompanyForm.value.otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.modifyCompanyForm.reset();
            this.tplModal.close();
          }
          if (environment.resetTimerWhenSubmit) {
            this.timerService.resetTimerSendOTP();
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  onSendDigitalAddressChange(): void {
    for (const i in this.modifyDigitalAddressForm.controls) {
      this.modifyDigitalAddressForm.controls[i].markAsDirty();
      this.modifyDigitalAddressForm.controls[i].updateValueAndValidity();
    }

    this.isSpinning = true;

    this.identityService.changeDigitalAddress(
      this.modifyDigitalAddressForm.value.addressChange,
      this.modifyDigitalAddressForm.value.otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.modifyDigitalAddressForm.reset();
            this.tplModal.close();
            if (environment.resetTimerWhenSubmit) {
              this.timerService.resetTimerSendOTP();
            }
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  onSendDocumentChange(): void {
    for (const i in this.modifyDocumentForm.controls) {
      this.modifyDocumentForm.controls[i].markAsDirty();
      this.modifyDocumentForm.controls[i].updateValueAndValidity({emitEvent: false});
    }

    this.isSpinning = true;
    const options = {year: 'numeric', month: '2-digit', day: '2-digit'};
    const issueDate = this.modifyDocumentForm.value.issueDateChange;
    const issueDateChangeStr = issueDate.toLocaleDateString('it-IT', options);
    const expirationDate = this.modifyDocumentForm.value.document_expiration_dateChange;
    const expirationDateStr = expirationDate.toLocaleDateString('it-IT', options);
    console.log(issueDateChangeStr, expirationDateStr);

    this.identityService.changeDocument(
      this.modifyDocumentForm.value.document_typeChange,
      this.modifyDocumentForm.value.document_numberChange,
      issueDateChangeStr,
      expirationDateStr,
      this.modifyDocumentForm.value.issuerTypeChange,
      this.modifyDocumentForm.value.issuerNameChange,
      this.modifyDocumentForm.value.issueCountryChange,
      this.modifyDocumentForm.value.otpCode)
      .subscribe(
        (response: GenericResponse) => {
          if (response.esito === Esito.Ok) {
            this.logger.log('Response OK');
            this.modifyDocumentForm.reset();
            this.tplModal.close();
            if (environment.resetTimerWhenSubmit) {
              this.timerService.resetTimerSendOTP();
            }
          }
        },
        (err: Observable<never>) => {
          this.isSpinning = false;
        },
        () => {
          this.isSpinning = false;
        },
      );
  }

  disabledFutureDate = (current: Date): boolean => {
    return current >= new Date();
  }

  disabledPastDate = (current: Date): boolean => {
    return current <= new Date();
  }

  ngOnDestroy(): void {
    this.tplModal?.close();
  }

  lookUpCountries(query: string): void {
    this.editIdentityService.onCountrySearch(query);
  }

  lookUpProvinces(query: string): void {
    this.editIdentityService.onProvinceSearch(query);
  }

  lookUpCities(query: string): void {
    this.editIdentityService.onCitySearch(query);
  }

  lookUpNameDocument(query: string): void {
    this.editIdentityService.onIssuerSearch(query);
  }

  activateSpin(value: boolean): void {
    this.isSpinning = value;
  }

  private onIssuerValueChange$(): Observable<any> {
    return this.editIdentityService.onIssuerValueChange$(this.issuerTypeCtrl).pipe(untilDestroyed(this));
  }

  get issuerTypeCtrl(): AbstractControl {
    return this.modifyDocumentForm.get('issuerTypeChange');
  }

  get issuerType(): string {
    return this.issuerTypeCtrl.value;
  }

  get issuerNameCtrl(): AbstractControl {
    return this.modifyDocumentForm.get('issuerNameChange');
  }

  get issuerName(): string {
    return this.issuerNameCtrl.value;
  }

  loadMoreIssuer(): void {
    this.editIdentityService.loadMoreIssuer(this.issuerType);
  }

  loadMoreCountries(): void {
    this.editIdentityService.loadMoreCountries();
  }

  get issuerTypeChangeCtrl(): AbstractControl {
    return this.modifyDocumentForm.get('issuerTypeChange');
  }

  private registerOnIssuerTypeChangeStatus$(): Observable<any> {
    return this.issuerTypeChangeCtrl.statusChanges.pipe(
      startWith(this.issuerTypeChangeCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.issuerNameCtrl.disable();
        } else if (status === 'VALID') {
          this.issuerNameCtrl.enable();
        }
      })
    );
  }

  private registerOnIssuerTypeChangeValue$(): Observable<any> {
    return this.issuerTypeCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.issuers = [];
        this.editIdentityService.onIssuerSearch('');
        this.issuerNameCtrl.patchValue(null);
      })
    );
  }

  private onCountryValueChange$(): Observable<any> {
    return this.editIdentityService.onCountryValueChange$().pipe(untilDestroyed(this));
  }

  get countryCtrl(): AbstractControl {
    return this.modifyAddressForm.get('countryChange');
  }

  get countryValue(): string {
    return this.countryCtrl.value;
  }

  get provinceCtrl(): AbstractControl {
    return this.modifyAddressForm.get('provinceChange');
  }

  get provinceValue(): string {
    return this.provinceCtrl.value;
  }

  get cityCtrl(): AbstractControl {
    return this.modifyAddressForm.get('cityChange');
  }

  get cityValue(): string {
    return this.cityCtrl.value;
  }

  private registerOnCountryStatusChange$(): Observable<any> {
    return this.countryCtrl.statusChanges.pipe(
      startWith(this.countryCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.provinceCtrl.disable();
          this.cityCtrl.disable();
        } else if (status === 'VALID') {
          this.provinceCtrl.enable();
        }
      })
    );
  }

  private registerOnCountryValueChange$(): Observable<any> {
    return this.countryCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.provincies = [];
        this.editIdentityService.onProvinceSearch('');
        this.editIdentityService.cities = [];
        this.editIdentityService.onCitySearch('');
        this.provinceCtrl.patchValue(null, {emitEvent: false});
        this.cityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private registerOnProvinceStatusChange$(): Observable<any> {
    return this.provinceCtrl.statusChanges.pipe(
      startWith(this.provinceCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.cityCtrl.disable();
        } else if (status === 'VALID') {
          this.cityCtrl.enable();
        }
      })
    );
  }

  private registerOnProvinceValueChange$(): Observable<any> {
    return this.provinceCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.cities = [];
        this.editIdentityService.onCitySearch('');
        this.cityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private onProvinceValueChange$(): Observable<any> {
    return this.editIdentityService.onProvinceValueChange$(this.countryCtrl).pipe(untilDestroyed(this));
  }

  private onCityValueChange$(): Observable<any> {
    return this.editIdentityService.onCityValueChange$(this.countryCtrl, this.provinceCtrl).pipe(untilDestroyed(this));
  }

  loadMoreProvincies(): void {
    this.editIdentityService.loadMoreProvincies(this.countryValue);
  }

  loadMoreCities(): void {
    this.editIdentityService.loadMoreCities(this.countryValue, this.provinceValue);
  }
}
